/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { computed, Injectable, Signal } from '@angular/core';
import { Store } from '@ngrx/store';
import moment from 'moment';
import AppError from '../model/app-error';
import { Country } from '../model/default-config.model';
import { CalculationAddOns } from '../model/offer';
import { PathResignation, PathTravelInPL, PathTripAbroad } from '../model/path.model';
import { PolicyholderPayload } from '../model/policyholder.model';
import { SaveCalculationResponse } from '../model/save-calculation.model';
import { AppState } from '../store';
import * as fromAgreements from './../store/agreements/agreements.reducer';
import * as fromAttachments from './../store/attachments/attachments.reducer';
import * as fromConfig from './../store/config/config.reducer';
import * as fromProcess from './../store/process/process.reducer';
import { FormsService } from './forms.service';
import { ProcessService, ProcessType } from './process.service';
import { peselToBirthDate } from '../utils';

enum PolicyTypeGtm {
  Polska = 'Polska',
  Zagranica = 'Zagranica',
  Rezygnacja = 'Rezygnacja',
}

@Injectable({
  providedIn: 'root',
})
export class GtmService {
  private processKind: Signal<ProcessType | null> = this.store.selectSignal(fromProcess.selectProcessKind);
  private selectedPackageName: Signal<string | undefined> = this.store.selectSignal(fromProcess.selectSelectedPackageName);
  private selectProcessAddonsSelection: Signal<CalculationAddOns | null> = this.store.selectSignal(fromProcess.selectProcessAddons);
  private countriesConfig: Signal<Country[] | undefined> = this.store.selectSignal(fromConfig.selectConfigTripAbroadCountries);
  private savedCalculation: Signal<SaveCalculationResponse | null> = this.store.selectSignal(fromProcess.selectSavedCalculation);
  private attachmentsError: Signal<AppError | null> = this.store.selectSignal(fromAttachments.selectAttachmentsError);
  private configError: Signal<AppError | null> = this.store.selectSignal(fromConfig.selectConfigError);
  private agreementsError: Signal<AppError | null> = this.store.selectSignal(fromAgreements.selectStatementsAgreementsError);
  private conclusionError: Signal<AppError | null> = this.store.selectSignal(fromProcess.selectProcessConclusionError);
  private savedCalculationError: Signal<AppError | null> = this.store.selectSignal(fromProcess.selectProcessSavedCalculationError);
  private savedOfferError: Signal<AppError | null> = this.store.selectSignal(fromProcess.selectProcessSavedOfferError);
  private offerQuotationError: Signal<AppError | null> = this.store.selectSignal(fromProcess.selectOfferQuotationError);
  private offerDiscountCodeError: Signal<AppError | null> = this.store.selectSignal(fromProcess.selectOfferDiscountCodeError);
  private calculationToClientConfirmError: Signal<AppError | null> = this.store.selectSignal(
    fromProcess.selectCalculationToClientConfirmError,
  );
  private processNavigationError: Signal<string | null> = this.store.selectSignal(fromProcess.selectProcessNavigationError);
  private travelInPl!: PathTravelInPL;
  private tripAbroad!: PathTripAbroad;
  private resignation!: PathResignation;
  private policyholder!: PolicyholderPayload;

  private selectedProcessAddons = computed(() => {
    const addons = this.selectProcessAddonsSelection();
    const selected = addons
      ? Object.entries(addons)
          .filter(([_, info]) => info.selected)
          .map(([name]) => name)
      : [];

    return selected;
  });

  private getPolicyType = computed(() => {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return PolicyTypeGtm.Polska;
      case ProcessType.TRIPABROAD:
        return PolicyTypeGtm.Zagranica;
      case ProcessType.RESIGNATION:
        return PolicyTypeGtm.Rezygnacja;
      default:
        return '';
    }
  });

  private getErrors = computed(() => {
    const errors = [
      this.attachmentsError(),
      this.configError(),
      this.agreementsError(),
      this.conclusionError(),
      this.savedCalculationError(),
      this.savedOfferError(),
      this.offerQuotationError(),
      this.offerDiscountCodeError(),
      this.calculationToClientConfirmError(),
    ].map((error) => error?.name);

    errors.push(this.processNavigationError() ?? undefined);

    return errors.filter((error) => !!error);
  });

  constructor(private store: Store<AppState>, private formsService: FormsService, private processService: ProcessService) {
    this.fetchForms();
  }

  private get destination(): string {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return '';
      case ProcessType.TRIPABROAD:
        return (
          this.countriesConfig()?.find((country) => country.countryId.toString() === this.tripAbroad.destination.countryId)?.countryName ??
          ''
        );
      case ProcessType.RESIGNATION:
        return (
          this.countriesConfig()?.find((country) => country.countryId.toString() === this.resignation.destination.countryId)?.countryName ??
          ''
        );
      default:
        return '';
    }
  }

  private get duration(): string {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return moment.duration(moment(this.travelInPl.travelDates.dateTo).diff(this.travelInPl.travelDates.dateFrom)).days().toString();
      case ProcessType.TRIPABROAD:
        return moment.duration(moment(this.tripAbroad.travelDates.dateTo).diff(this.tripAbroad.travelDates.dateFrom)).days().toString();
      case ProcessType.RESIGNATION:
        return moment.duration(moment(this.resignation.travelDates.dateTo).diff(this.resignation.travelDates.dateFrom)).days().toString();
      default:
        return '';
    }
  }

  private get participantsNo(): string {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return this.travelInPl.travelersPerBirthYear.numberOfInsureds.toString();
      case ProcessType.TRIPABROAD:
        return this.tripAbroad.travelersPerBirthYear.numberOfInsureds.toString();
      case ProcessType.RESIGNATION:
        return this.resignation.numberOfInsureds.toString();
      default:
        return '';
    }
  }

  private get riskSports(): string {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return this.travelInPl.coverages.extremeSports?.selected ? 'y' : 'n';
      case ProcessType.TRIPABROAD:
        return this.tripAbroad.coverages.extremeSports?.selected ? 'y' : 'n';
      default:
        return '';
    }
  }

  private get insuranceStartDate(): string {
    switch (this.processKind()) {
      case ProcessType.TRAVELINPL:
        return this.travelInPl.travelDates.dateFrom;
      case ProcessType.TRIPABROAD:
        return this.tripAbroad.travelDates.dateFrom;
      case ProcessType.RESIGNATION:
        return this.resignation.travelDates.dateFrom;
      default:
        return '';
    }
  }

  private get userAge(): string {
    const birthDate = peselToBirthDate(this.policyholder.pesel) ?? '';
    const currentYear = moment();
    let age = '';

    if (birthDate !== '') {
      age = currentYear.diff(moment(birthDate), 'years').toString();
    }

    return age;
  }

  step1Event() {
    this.sendEvent({
      event: 'step',
      stepName: 'insurance type',
      stepNo: '1',
      partner: this.getPartner(),
      hashPage: 'step1',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
    });
  }

  step2Event() {
    this.sendEvent({
      event: 'step',
      stepName: 'travel information',
      stepNo: '2' + (this.getPolicyType() === PolicyTypeGtm.Rezygnacja ? 'a' : ''),
      partner: this.getPartner(),
      hashPage: 'step2' + (this.getPolicyType() === PolicyTypeGtm.Rezygnacja ? 'a' : ''),
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
    });
  }

  step3Event() {
    this.fetchForms();

    const eventToSend: { [key: string]: string } = {
      event: 'step',
      stepName: 'quotation',
      stepNo: '3',
      partner: this.getPartner(),
      hashPage: 'step3',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
      destination: this.destination,
      duration: this.duration,
      participantsNo: this.participantsNo,
      quotationID: this.processService.apiPayload.interactionData.sessionId,
    };

    if (this.getPolicyType() === PolicyTypeGtm.Rezygnacja) {
      eventToSend['stepNo'] = '3a';
      eventToSend['hashPage'] = 'step3a';
      eventToSend['tripValue'] = this.resignation.totalTravelPrice.toString();
      eventToSend['tripDate'] = this.resignation.travelPurchaseDate;
    } else {
      eventToSend['riskSports'] = this.riskSports;
    }

    this.sendEvent(eventToSend);
  }

  step4Event() {
    this.fetchForms();

    const eventToSend: { [key: string]: string } = {
      event: 'step',
      stepName: 'contract data',
      stepNo: '4',
      partner: this.getPartner(),
      hashPage: 'step4',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
      destination: this.destination,
      duration: this.duration,
      participantsNo: this.participantsNo,
      quotationID: this.processService.apiPayload.interactionData.sessionId,
      packageName: this.selectedPackageName() ?? '',
      additionalProtection: this.selectedProcessAddons().toString(),
    };

    if (this.getPolicyType() === PolicyTypeGtm.Rezygnacja) {
      eventToSend['stepNo'] = '4a';
      eventToSend['hashPage'] = 'step4a';
      eventToSend['tripValue'] = this.resignation.totalTravelPrice.toString();
      eventToSend['tripDate'] = this.resignation.travelPurchaseDate;
    } else {
      eventToSend['riskSports'] = this.riskSports;
    }

    this.sendEvent(eventToSend);
  }

  step5Event() {
    this.fetchForms();
    const eventToSend: { [key: string]: string } = {
      event: 'step',
      stepName: 'summary',
      stepNo: '5',
      partner: this.getPartner(),
      hashPage: 'step5',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
      destination: this.destination,
      duration: this.duration,
      participantsNo: this.participantsNo,
      quotationID: this.processService.apiPayload.interactionData.sessionId,
      packageName: this.selectedPackageName() ?? '',
      additionalProtection: this.selectedProcessAddons().toString(),
      city: this.policyholder.address.city,
      entrepreneur: this.policyholder.isCompany ? 'y' : 'n',
      userAge: this.userAge,
    };

    if (this.getPolicyType() === PolicyTypeGtm.Rezygnacja) {
      eventToSend['stepNo'] = '5a';
      eventToSend['hashPage'] = 'step5a';
      eventToSend['tripValue'] = this.resignation.totalTravelPrice.toString();
      eventToSend['tripDate'] = this.resignation.travelPurchaseDate;
    } else {
      eventToSend['riskSports'] = this.riskSports;
    }

    this.sendEvent(eventToSend);
  }

  step6Event() {
    this.fetchForms();
    const eventToSend: { [key: string]: string } = {
      event: 'step',
      stepName: 'consents-and-declarations',
      stepNo: '6',
      partner: this.getPartner(),
      hashPage: 'step6',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
      destination: this.destination,
      duration: this.duration,
      participantsNo: this.participantsNo,
      quotationID: this.processService.apiPayload.interactionData.sessionId,
      packageName: this.selectedPackageName() ?? '',
      additionalProtection: this.selectedProcessAddons().toString(),
      city: this.policyholder.address.city,
      entrepreneur: this.policyholder.isCompany ? 'y' : 'n',
      userAge: this.userAge,
    };

    if (this.getPolicyType() === PolicyTypeGtm.Rezygnacja) {
      eventToSend['stepNo'] = '6a';
      eventToSend['hashPage'] = 'step6a';
      eventToSend['tripValue'] = this.resignation.totalTravelPrice.toString();
      eventToSend['tripDate'] = this.resignation.travelPurchaseDate;
    } else {
      eventToSend['riskSports'] = this.riskSports;
    }

    this.sendEvent(eventToSend);
  }

  step7Event() {
    this.fetchForms();

    const eventToSend: { [key: string]: string } = {
      event: 'step',
      stepName: 'thank-you-page',
      stepNo: '7',
      partner: this.getPartner(),
      hashPage: 'step7',
      product: 'Travel',
      formName: 'Q&B Travel',
      errorName: this.getErrors().toString(),
      policyType: this.getPolicyType(),
      destination: this.destination,
      duration: this.duration,
      participantsNo: this.participantsNo,
      quotationID: this.processService.apiPayload.interactionData.sessionId,
      packageName: this.selectedPackageName() ?? '',
      additionalProtection: this.selectedProcessAddons().toString(),
      city: this.policyholder.address.city,
      entrepreneur: this.policyholder.isCompany ? 'y' : 'n',
      userAge: this.userAge,
      policyID: this.savedCalculation()?.result?.policyNumber.toString() ?? '',
      policyValue: this.savedCalculation()?.result?.calculatedPremium.toString() ?? '',
      insuranceStartDate: this.insuranceStartDate,
    };

    if (this.getPolicyType() === PolicyTypeGtm.Rezygnacja) {
      eventToSend['stepNo'] = '7a';
      eventToSend['hashPage'] = 'step7a';
      eventToSend['tripValue'] = this.resignation.totalTravelPrice.toString();
      eventToSend['tripDate'] = this.resignation.travelPurchaseDate;
    } else {
      eventToSend['riskSports'] = this.riskSports;
    }

    this.sendEvent(eventToSend);
  }

  private fetchForms() {
    this.travelInPl = this.formsService.travelInPlApi;
    this.tripAbroad = this.formsService.tripAbroadApi;
    this.resignation = this.formsService.resignationApi;
    this.policyholder = this.formsService.policyholderApi;
  }

  private getPartner(): string {
    const url = new URL(window.location.href);

    return (url.searchParams.get('partner') as string) ?? '';
  }

  private sendEvent({ ...event }) {
    (window as any).dataLayer = (window as any).dataLayer || [];
    (window as any).dataLayer.push(event);
  }
}
